import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isObject, map } from 'lodash';
import classnames from 'classnames';
import { emojify } from 'react-emojione';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';

import configService from '@lib/services/configService';
import UserAvatar from 'core/UserAvatar';

import { muiThemeLuma } from 'themes';
import VoicePlayer from 'components/VoicePlayer';
import AvatarIcon from 'components/AvatarIcon';
import { hasAccessToProduct } from 'utils/permissions';
import IconAvatarBot from 'icons/IconAvatarBot';
import StatusIcon from './StatusIcon';

import { REF_PROVISIONING, VISIBILITY_INTERNAL } from './constants';

const styles = (theme) => {
  const {
    custom: {
      palette: {
        accent1Color,
        colorDarkGray,
        colorGrayishBlue,
        colorAccentSunrise,
        colorAccentPeach,
        colorMessageBubble,
      },
    },
    palette: {
      common: { white },
      primary,
      text,
    },
    spacing: { unit },
  } = theme;

  return {
    avatarBot: {
      color: '#fff',
      backgroundColor: colorMessageBubble,
      width: 26,
      height: 26,
    },
    attachButton: {
      backgroundColor: `${white} !important`,
      height: 25,
      marginTop: 5,
      padding: `0px ${unit * 2}px`,
    },
    attachButtonLabel: {
      color: `${text.primary} !important`,
      fontSize: 11,
      textTransform: 'initial',
    },
    attachIcon: {
      fontSize: 14,
    },
    broadcast: {},
    chatAudit: {},
    communicationInfoIcon: {
      color: white,
      fontSize: 13,
      position: 'relative',
      top: unit / -4,
      verticalAlign: 'middle',
      '&$broadcast': {
        color: `${accent1Color} !important`,
      },
      '&$primary': {
        color: `${text.primary} !important`,
      },
    },
    communicationInfoWrapper: {
      display: 'inline-block',
      float: 'left',
    },
    datetime: {
      display: 'inline-block',
      float: 'left',
      padding: `0px ${unit / 2}px`,
    },
    typing: {},
    typingIndicator: {
      backgroundColor: primary.main,
      borderRadius: 5,
      display: 'table',
      marginTop: unit / 4,
      padding: unit,
      position: 'relative',
      width: 'auto',
      willChange: 'transform',
      '& span': {
        height: 4,
        width: 4,
        float: 'left',
        margin: '0 1px',
        backgroundColor: white,
        display: 'block',
        borderRadius: '50%',
        opacity: '0.8',
        '&:nth-of-type(1)': {
          animation: '1s blink infinite 0.3s',
        },
        '&:nth-of-type(2)': {
          animation: '1s blink infinite 0.6s',
        },
        '&:nth-of-type(3)': {
          animation: '1s blink infinite 0.9s',
        },
      },
    },
    interlocutor: {
      color: text.secondary,
      display: 'inline-block',
      float: 'right',
      fontSize: 13,
      fontWeight: 500,
      padding: `${unit / 2}px ${unit / 4 + 4}px ${unit + 4}px 0px`,
      textAlign: 'right',
      width: '100%',
    },
    internal: {},
    messageContent: {
      color: 'inherit',
      fontWeight: 'inherit',
      width: '100%',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      wordWrap: 'break-word',
      '& a': {
        color: 'inherit !important',
      },
    },
    messageIcon: {
      position: 'absolute',
      width: 35,
      height: 35,
      left: -18,
      top: -18,
      border: '1px solid',
      '& span': {
        fontSize: 18,
      },
    },
    messageInfoVoicePlayer: {
      display: 'inline-block',
      float: 'left',
      padding: `0px ${unit / 2}px`,
    },
    messageInfoWrapper: {
      display: 'inline-block',
      fontSize: 10,
      lineHeight: '16px',
    },
    messageWrapper: {
      alignItems: 'flex-start',
      backgroundColor: colorGrayishBlue,
      position: 'relative',
      border: '1px',
      borderRadius: '15px 15px 15px 0px',
      color: text.primary,
      display: 'flex',
      flexDirection: 'column',
      fontWeight: 500,
      marginLeft: unit / 2,
      marginRight: 0,
      maxWidth: '80%',
      padding: '10px 15px',
      '&$outbound': {
        backgroundColor: colorMessageBubble,
        borderRadius: '15px 15px 0px 15px !important',
        color: `${white} !important`,
        alignItems: 'flex-end !important',
        marginLeft: '0px !important',
        marginRight: `${unit / 2}px !important`,
      },
      '&$internal': {
        backgroundColor: `${colorAccentSunrise} !important`,
        border: `1px solid ${colorAccentPeach} !important`,
        color: `${text.primary} !important`,
      },
      '&$broadcast': {
        backgroundColor: `${text.primary} !important`,
        color: `${white} !important`,
      },
      '&$typing': {
        backgroundColor: 'initial !important',
        padding: '0px !important',
      },
    },
    outbound: {},
    primary: {},
    pullBottom: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 5,
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      '&$outbound': {
        justifyContent: 'flex-end !important',
      },
      '&$chatAudit': {
        color: colorDarkGray,
        padding: unit * 2,
        fontSize: 14,
        fontWeight: 500,
        justifyContent: 'center !important',
      },
    },
    '@keyframes blink': {
      '50%': {
        transform: 'scale(1.5)',
      },
    },
  };
};

const isOutbound = ({ type }) => type !== 'inbound';

class Message extends Component {
  renderFile() {
    const {
      classes,
      currentUser,
      message: { file },
    } = this.props;

    if (!file) return;

    const isUploading = isObject(file);

    return (
      <Button
        classes={{ root: classes.attachButton, label: classes.attachButtonLabel }}
        disabled={isUploading}
        href={`${configService.getApiBasePath()}/fileUploads/${file}/file?token=${currentUser.token}`}
        target="blank"
        variant="contained"
      >
        <AttachFileIcon className={classes.attachIcon} />
        {isUploading ? 'File uploading...' : 'File attached'}
      </Button>
    );
  }

  renderCommunicationInfo(me, outbound, message) {
    const { classes } = this.props;
    let icon = '';
    let tip = '';

    // don't display communication info to staff to staff messages
    if (message.visibility === VISIBILITY_INTERNAL) return false;

    if (message.ref !== 'broadcast') {
      switch (message.channel) {
        case 'chat':
          if (message.secure) {
            icon = 'lock';
            tip = 'Secure Chat';
          }
          break;
        case 'voice':
          icon = 'phone';
          tip = 'Voice Call';
          break;
        case 'email':
          icon = 'email';
          tip = 'Email';
          break;
        default:
      }
    } else {
      icon = 'rss_feed';
      tip = 'Broadcast message';
    }

    if (icon === '') return;

    return (
      <div className={classes.communicationInfoWrapper}>
        <Icon
          className={classnames(classes.communicationInfoIcon, {
            [classes.primary]: !outbound || message.visibility === VISIBILITY_INTERNAL,
            [classes.broadcast]: message.ref === 'broadcast',
          })}
        >
          {icon}
        </Icon>
        {` ${tip} `}
        &mdash;
      </div>
    );
  }

  renderVoicePlayer(outbound, message) {
    const { classes } = this.props;
    const color =
      !outbound || message.visibility === VISIBILITY_INTERNAL
        ? muiThemeLuma.palette.common.black
        : muiThemeLuma.palette.common.white;

    if (!(message.ref !== 'broadcast' && message.channel === 'voice' && message.media && message.media.url)) return;

    return (
      <div className={classes.messageInfoVoicePlayer}>
        <VoicePlayer color={color} media={message.media.url} />
      </div>
    );
  }

  renderMessageInfo() {
    const { classes, currentUser, interlocutor, message, typing } = this.props;

    if (typing) return;

    const me = interlocutor._id === currentUser._id;
    const outbound = isOutbound(message);
    const createdAt = moment(message.createdAt);
    const timestamp = createdAt.format('MMM DD, YYYY h:mm A');

    return (
      <div className={classes.messageInfoWrapper}>
        {this.renderVoicePlayer(outbound, message)}
        {this.renderCommunicationInfo(me, outbound, message)}
        <div className={classes.datetime}>{timestamp}</div>
        <StatusIcon isOutbound={outbound} message={message} />
      </div>
    );
  }

  renderText() {
    const { classes, message, permissions, typing } = this.props;
    const outbound = isOutbound(message);

    if (typing) {
      return (
        <div className={classnames(classes.messageWrapper, classes.typing)}>
          <div className={classes.typingIndicator}>
            <span />
            <span />
            <span />
          </div>
        </div>
      );
    }

    let messageHtml = message.text;

    if (message.type === 'outbound' || message.ref === 'chat') {
      if (message.mentions && message.mentions.length > 0) {
        const mentionsPattern = new RegExp(`\\B(${map(message.mentions, 'mention').join('|')})\\b`, 'g');

        messageHtml = messageHtml.replace(
          mentionsPattern,
          (mention) =>
            `<span style="background-color: #fff; border-radius: 12px; color: #3460C5; font-weight: inherit; padding: 0px 4px; white-space: normal">
            ${mention}
          </span>`,
        );
      }

      // highlight templates
      messageHtml = messageHtml.replace(
        /({{[^}]+}})/gm,
        '<span style="background-color: #f1f1f1; border-radius: 4px; color: #2595A5; font-weight: 400;">$1</span>',
      );
    }

    if (permissions.respectProvisioning) {
      const messageProvisioning = REF_PROVISIONING[message.ref];

      if (messageProvisioning && !hasAccessToProduct(permissions, messageProvisioning.provisioning)) {
        messageHtml = 'Message suppressed for patient privacy';
      }
    }

    messageHtml = messageHtml.replace(/(:[\w]+:)/gm, (match) => {
      const emoji = emojify(match, { output: 'unicode' });

      return `<span style="font-size: 14px;">${emoji}</span>`;
    });

    return (
      <div
        className={classnames(classes.messageWrapper, {
          [classes.outbound]: outbound,
          [classes.internal]: message.visibility === VISIBILITY_INTERNAL,
          [classes.broadcast]: message.ref === 'broadbast',
        })}
      >
        {message.icon && <AvatarIcon className={classes.messageIcon}>{message.icon}</AvatarIcon>}
        <div
          className={classes.messageContent}
          dangerouslySetInnerHTML={{ __html: messageHtml }} // eslint-disable-line react/no-danger
        />
        {this.renderMessageInfo()}
        {this.renderFile()}
      </div>
    );
  }

  renderUser() {
    const { classes, interlocutor, message } = this.props;
    const { type, automated } = message;
    if (type === 'outbound' && automated) {
      return (
        <div className={classes.pullBottom}>
          <Avatar className={classes.avatarBot}>
            <IconAvatarBot fontSize="small" />
          </Avatar>
        </div>
      );
    }

    let user = null;

    if (interlocutor.master) {
      user = {
        ...interlocutor,
        name: `${interlocutor.firstname} ${interlocutor.lastname}`,
      };
    }

    return (
      <div className={classes.pullBottom}>
        <UserAvatar user={user || interlocutor} size="small" />
      </div>
    );
  }

  renderItem() {
    const { classes, message } = this.props;
    const outbound = isOutbound(message);
    let messageHtml = message.text;

    if (/chat-audit|chatAuditTz/.test(message.ref)) {
      messageHtml = `${messageHtml} — ${moment(message.createdAt).format('lll')}`;
      return (
        <Typography
          className={classnames(classes.wrapper, classes.chatAudit)}
          component="div"
          dangerouslySetInnerHTML={{ __html: messageHtml }} // eslint-disable-line react/no-danger
        />
      );
    }

    return (
      <div className={classnames(classes.wrapper, { [classes.outbound]: outbound })}>
        {!outbound && this.renderUser()}
        {this.renderText()}
        {outbound && this.renderUser()}
      </div>
    );
  }

  renderInterlocutorName() {
    const { classes, interlocutor, message } = this.props;
    const { automated } = message;

    if (!isOutbound(message) || /chat-audit|chatAuditTz/.test(message.ref)) return;

    let name = interlocutor.name;

    if (automated) {
      name = 'via Luma';
    } else if (interlocutor.master) {
      const { firstname, lastname } = interlocutor;
      name = `${firstname} ${lastname}`;
    }

    return <Typography className={classes.interlocutor}>{name}</Typography>;
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        {this.renderItem()}
        {this.renderInterlocutorName()}
      </div>
    );
  }
}

Message.propTypes = {
  classes: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  interlocutor: PropTypes.object.isRequired,
  message: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  typing: PropTypes.bool,
};

Message.defaultProps = {
  typing: false,
};

export default withStyles(styles)(Message);
